import { Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '@app/ui/dropdown';

export interface ApplicationSwitcherOption {
  readonly id: number;
  readonly name: string;
  readonly meta: string;
}

export interface ApplicationSwitcherProps {
  readonly options: ApplicationSwitcherOption[];
  readonly value?: number;
  readonly onChange: (id: number) => void;
}

// SAA-112 P-K, design pass (Claude Design `ISC App.dc.html`) — shared by the client and internal
// switcher (4 call sites): a bordered trigger box (not a themed Button) opening a list of
// name+"Created: date" rows, current selection marked with a radio glyph. No "Latest" badge —
// removed deliberately in the design pass (D12/R7). Values not in the theme are hardcoded per the
// extraction table (`#EEF0F3`, `#FBEAF5`).
export const ApplicationSwitcher = ({ options, value, onChange }: ApplicationSwitcherProps) => {
  const { t } = useTranslation('common');
  const current = options.find(option => option.id === value);

  if (options.length < 2) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.4, color: 'text.secondary' }}>
        {t('application.field.switcher.label')}
      </Typography>
      <Dropdown
        label={current?.name ?? ''}
        popperWidth={400}
        popperPlacement="bottom-end"
        popperOffset={8}
        listMaxHeight="none"
        paperSx={{
          py: 0,
          my: 0,
          border: '1px solid #EEF0F3',
          boxShadow: '0 4px 16px rgba(51,51,51,0.16)',
          overflow: 'hidden',
        }}
        itemSx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          textAlign: 'left',
          gap: 1.5,
          py: 2,
          px: 2,
          my: 0,
          borderRadius: 0,
          borderBottom: '1px solid #EEF0F3',
          '&:last-of-type': { borderBottom: 'none' },
          '&:hover': { bgcolor: 'grey.200' },
        }}
        header={
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #EEF0F3' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: 'text.secondary' }}>
              {t('application.field.switcher.listTitle')}
            </Typography>
          </Box>
        }
        options={options.map(option => ({
          id: option.id,
          onClick: () => onChange(option.id),
          name: (
            <>
              {option.id === value ? (
                <RadioButtonCheckedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 400, lineHeight: 1.4, color: 'text.primary' }}>
                  {option.name}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 400, lineHeight: 1.4, color: 'text.secondary' }}>
                  {option.meta}
                </Typography>
              </Box>
            </>
          ),
        }))}
        renderTrigger={({ open }) => (
          <Box
            sx={{
              width: 150,
              height: 40,
              borderRadius: 2,
              bgcolor: 'background.paper',
              boxShadow: theme => `inset 0 0 0 1px ${theme.palette.grey[500]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              boxSizing: 'border-box',
              cursor: 'pointer',
            }}
          >
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.4, color: 'text.primary' }}>
              {current?.name}
            </Typography>
            {open ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            )}
          </Box>
        )}
      />
    </Box>
  );
};
